import type { FC, ReactNode } from 'react'
import type { ActivityPill } from '@/constants/home'

import { useEffect, useState } from 'react'
import { Reveal } from '@/components/generals/reveal'
import { Marquee } from '@/components/generals/marquee'
import { AppIcon } from '@/components/generals/app-icon'
import { PillButton } from '@/components/generals/pill-button'
import { SlidingNumber } from '@/components/generals/sliding-number'
import { useContact } from '@/components/generals/contact-dialog'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { activityRows, annualFigures } from '@/constants/home'

interface PillState {
    count: number
    time: string
}

const formatCount = (value: number): string => value.toLocaleString('en-US')

const Pill: FC<{ pill: ActivityPill, state: PillState, play: boolean }> = ({ pill, state, play }): ReactNode => (
    <div className='flex shrink-0 items-center gap-3 rounded-full bg-white py-2 pl-2 pr-5 shadow-[0_1px_2px_rgba(14,14,14,0.04),0_10px_22px_-14px_rgba(14,14,14,0.22)] ring-1 ring-[#ebebf0]'>
        <AppIcon name={pill.app} size='sm' shape='circle' />

        <div className='flex items-center gap-1.5 whitespace-nowrap text-[14px] text-ink'>
            <SlidingNumber value={formatCount(state.count)} play={play} duration={1300} stagger={80} className='font-semibold' />
            <span>{pill.label}</span>
        </div>

        <span className='whitespace-nowrap text-[12.5px] text-silver'>
            {state.time}
        </span>
    </div>
)

const initialStates = (): Array<Array<PillState>> => activityRows.map(row => row.map(pill => ({ count: pill.count, time: pill.time })))

export const NumbersSection: FC = (): ReactNode => {
    const { openContact } = useContact()
    const reduced = useReducedMotion()
    const rows = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })
    const stats = useInView<HTMLDListElement>({ rootMargin: '0px 0px -10% 0px' })
    const [pillStates, setPillStates] = useState<Array<Array<PillState>>>(initialStates)
    const [apiCalls, setApiCalls] = useState(3104882415)

    // Every couple of seconds one workflow reports new activity and its counter rolls up.
    useEffect(() => {
        if (!rows.inView || reduced) return

        const timer = window.setInterval(() => {
            setPillStates(previous => {
                const rowIndex = Math.floor(Math.random() * previous.length)
                const pillIndex = Math.floor(Math.random() * previous[rowIndex].length)

                return previous.map((row, currentRow) => row.map((state, currentPill) => (
                    currentRow === rowIndex && currentPill === pillIndex
                        ? { count: state.count + 1 + Math.floor(Math.random() * 6), time: 'just now' }
                        : state
                )))
            })
        }, 2200)

        return () => window.clearInterval(timer)
    }, [rows.inView, reduced])

    useEffect(() => {
        if (!stats.inView || reduced) return

        const timer = window.setInterval(() => {
            setApiCalls(previous => previous + 9 + Math.floor(Math.random() * 40))
        }, 3200)

        return () => window.clearInterval(timer)
    }, [stats.inView, reduced])

    return (
        <section id='numbers' aria-labelledby='numbers-title' className='border-y border-hairline bg-[#fafafb]'>
            <div className='fc-container pb-14 pt-24 md:pb-16 md:pt-32'>
                <Reveal className='flex max-w-[620px] flex-col items-start gap-6'>
                    <h2 id='numbers-title' className='text-[clamp(2.25rem,4.6vw,4rem)] font-medium leading-[1.04] tracking-[-0.04em]'>
                        <span className='text-ink'>The numbers</span>{' '}
                        <span className='text-silver'>speak for themselves.</span>
                    </h2>

                    <p className='max-w-[520px] text-[17px] leading-relaxed text-slate'>
                        Behind each update is a real production workflow, moving data between business systems and keeping operations running every minute.
                    </p>

                    <PillButton chevron onClick={() => openContact({ intent: 'demo' })} className='mt-2'>
                        Book a demo
                    </PillButton>
                </Reveal>
            </div>

            <div ref={rows.ref} className='flex flex-col gap-4 pb-20 md:pb-24' aria-label='Live workflow activity'>
                {activityRows.map((row, rowIndex) => (
                    <Marquee
                        key={rowIndex}
                        duration={rowIndex === 0 ? 85 : 95}
                        reverse={rowIndex === 1}
                        trackClassName='gap-4 pr-4 py-1'
                        className='[--fc-fade:140px]'
                    >
                        {row.map((pill, pillIndex) => (
                            <Pill key={`${pill.app}-${pill.label}`} pill={pill} state={pillStates[rowIndex][pillIndex]} play={rows.inView} />
                        ))}
                    </Marquee>
                ))}
            </div>

            <div className='fc-container'>
                <div className='relative border-t border-hairline'>
                    <span className='absolute left-1/2 top-0 inline-flex h-7 -translate-x-1/2 -translate-y-1/2 items-center rounded-full bg-white px-3.5 text-[12.5px] font-medium text-slate ring-1 ring-inset ring-[#e3e3e9]'>
                        Annual figures
                    </span>

                    <dl ref={stats.ref} className='grid grid-cols-1 gap-y-10 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8 lg:py-16'>
                        {annualFigures.map(figure => {
                            const value = figure.label === 'API calls' ? formatCount(apiCalls) : figure.value

                            return (
                                <div key={figure.label} className='flex flex-col gap-3'>
                                    <dt className='text-[14px] text-slate'>
                                        {figure.label}
                                    </dt>

                                    <dd className='text-[clamp(1.75rem,2.5vw,2.25rem)] font-medium tracking-[-0.03em] text-ink'>
                                        <SlidingNumber value={value} play={stats.inView} duration={1800} stagger={55} />
                                    </dd>
                                </div>
                            )
                        })}
                    </dl>
                </div>
            </div>
        </section>
    )
}
